<template>
  <div class="flex whitespace-break-spaces">
    <div
      v-if="hasDeliveryBeforeChristmas"
      class="align-content-center-safe p-6"
    >
      <RevIllustration
        alt=""
        :height="24"
        src="/img/checkout/christmasTruck.svg"
        :width="24"
      />
    </div>

    <div class="align-content-center-safe pl-6">
      <div class="body-2">
        {{ standardDelivery }}
      </div>

      <div
        v-if="hasExpressDelivery"
        class="caption"
        :class="{
          'text-static-success-hi':
            !isStandardDeliveryBeforeChristmas &&
            isExpressDeliveryBeforeChristmas,
        }"
      >
        {{ expressDelivery }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

import type { ShippingChoice } from '@backmarket/http-api/src/api-specs-checkout/cart/cart.types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevIllustration } from '@ds/components/Illustration'

import { useDeliveryBeforeChristmas } from '~/scopes/product/composables/useDeliveryBeforeChristmas'

import translations from './ShippingPromiseChristmas.translations'

type DeliveryData = {
  earliest: string
  latest: string
  amount: string
}

const props = defineProps<{
  choices: ShippingChoice[]
  freeDeliveryData: DeliveryData
  expressDeliveryData: DeliveryData
}>()

const i18n = useI18n()

const shippingData = computed(() => {
  const freeDate = props.choices.find(
    (choice) => choice.price === 0,
  )?.latestArrivalDate
  const expressDate = props.choices.find(
    (choice) => choice.price !== 0,
  )?.latestArrivalDate

  return {
    free:
      freeDate && freeDate.length > 0
        ? { latestArrivalDate: freeDate }
        : undefined,
    express:
      expressDate && expressDate.length > 0
        ? { latestArrivalDate: expressDate }
        : undefined,
  }
})

const {
  hasDeliveryBeforeChristmas,
  isStandardDeliveryBeforeChristmas,
  isExpressDeliveryBeforeChristmas,
} = useDeliveryBeforeChristmas(shippingData.value)

const hasExpressDelivery = computed(() =>
  props.choices.some((choice) => choice.price !== 0),
)

const standardDelivery = computed(() => {
  if (isStandardDeliveryBeforeChristmas.value) {
    return i18n(
      translations.shippingPromiseBeforeChristmas,
      props.freeDeliveryData,
    )
  }

  return i18n(translations.shippingPromise, props.freeDeliveryData)
})

const expressDelivery = computed(() => {
  if (isExpressDeliveryBeforeChristmas.value) {
    return i18n(
      translations.shippingPromiseExpressBeforeChristmas,
      props.expressDeliveryData,
    )
  }

  return i18n(translations.shippingPromiseExpress, props.expressDeliveryData)
})
</script>
